import { useState, useEffect, useCallback, useRef } from 'react';
import { boardApi } from '../services/BoardApiService';
import { useEventDeduplication } from './useEventDeduplication';
import type { Board, BoardParticipant, Card, Column, BoardSettings, BoardSettingsUpdate } from '../types/board';

interface UseBoardOptions {
  boardId: string;
  userId: string;
  userName?: string;
}

interface UseBoardReturn {
  board: Board | null;
  isLoading: boolean;
  error: string | null;
  isConnected: boolean;
  reconnectAttempts: number;
  isAdmin: boolean;
  participants: BoardParticipant[];
  requiresPassword: boolean;
  requiresUserName: boolean;
  hasJoined: boolean;
  joinError: string | null;
  revealHiddenCards: () => Promise<void>;
  hideHiddenCards: () => Promise<void>;
  addCard: (columnId: string, content: string, author: string) => Promise<void>;
  moveCard: (cardId: string, targetColumnId: string, targetIndex?: number) => Promise<void>;
  updateCard: (cardId: string, content: string) => Promise<void>;
  deleteCard: (cardId: string) => Promise<void>;
  removeCard: (cardId: string) => void;
  voteCard: (cardId: string) => Promise<void>;
  removeCardVote: (cardId: string) => Promise<void>;
  addColumn: (name: string, color: string) => Promise<void>;
  updateColumn: (columnId: string, name: string, color: string) => Promise<void>;
  deleteColumn: (columnId: string, migrateToColumnId?: string) => Promise<void>;
  reorderColumns: (columnIds: string[]) => Promise<void>;
  updateBoardSettings: (settings: BoardSettingsUpdate) => Promise<void>;
  promoteToAdmin: (adminPassword: string) => Promise<void>;
  mergeCards: (sourceCardId: string, targetCardId: string) => Promise<void>;
  joinBoard: (password?: string, userName?: string) => Promise<void>;
  updateUserBoardName: (userName: string) => Promise<string>;
}

function toBoardSettingsPatch(settings: BoardSettingsUpdate): Partial<BoardSettings> {
  const settingsPatch: Partial<BoardSettings> = {};
  if (settings.cardVisibility !== undefined) settingsPatch.cardVisibility = settings.cardVisibility;
  if (settings.maxColumns !== undefined) settingsPatch.maxColumns = settings.maxColumns;
  if (settings.maxCardsPerColumn !== undefined) settingsPatch.maxCardsPerColumn = settings.maxCardsPerColumn;
  if (settings.showAuthorForParticipants !== undefined) settingsPatch.showAuthorForParticipants = settings.showAuthorForParticipants;
  if (settings.showAuthorForAdmin !== undefined) settingsPatch.showAuthorForAdmin = settings.showAuthorForAdmin;
  if (settings.showCardDate !== undefined) settingsPatch.showCardDate = settings.showCardDate;
  if (settings.enableMergeCards !== undefined) settingsPatch.enableMergeCards = settings.enableMergeCards;
  if (settings.enableCardGroups !== undefined) settingsPatch.enableCardGroups = settings.enableCardGroups;
  if (settings.votingEnabled !== undefined) settingsPatch.votingEnabled = settings.votingEnabled;
  if (settings.votesPerParticipant !== undefined) settingsPatch.votesPerParticipant = settings.votesPerParticipant;
  if (settings.allowMultipleVotesPerCard !== undefined) settingsPatch.allowMultipleVotesPerCard = settings.allowMultipleVotesPerCard;
  if (settings.showVotesToParticipants !== undefined) settingsPatch.showVotesToParticipants = settings.showVotesToParticipants;
  if (settings.requireNameOnJoin !== undefined) settingsPatch.requireNameOnJoin = settings.requireNameOnJoin;
  if (settings.allowAnonymous !== undefined) settingsPatch.allowAnonymous = settings.allowAnonymous;
  return settingsPatch;
}

function isUnjoinedConnectionError(error: unknown): boolean {
  return error instanceof Error && error.message === 'Connection has not joined this board';
}

interface ParticipantEventPayload {
  userId?: string;
  userName?: string;
  role?: string;
  isSelf?: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function readParticipantEventPayload(payload: unknown): ParticipantEventPayload {
  if (!isRecord(payload)) return {};
  return {
    userId: typeof payload.userId === 'string' ? payload.userId : undefined,
    userName: typeof payload.userName === 'string' ? payload.userName : undefined,
    role: typeof payload.role === 'string' ? payload.role : undefined,
    isSelf: typeof payload.isSelf === 'boolean' ? payload.isSelf : undefined,
  };
}

function normalizeParticipantName(name: string | undefined): string {
  const trimmedName = name?.trim();
  return trimmedName && trimmedName.length > 0 ? trimmedName : 'Anonymous';
}

function sortParticipants(items: BoardParticipant[]): BoardParticipant[] {
  return [...items].sort((first, second) => {
    if (first.isAdmin && !second.isAdmin) return -1;
    if (!first.isAdmin && second.isAdmin) return 1;
    return first.name.localeCompare(second.name);
  });
}

function upsertParticipant(
  participants: BoardParticipant[],
  participant: BoardParticipant
): BoardParticipant[] {
  const participantsById = new Map<string, BoardParticipant>();
  participants.forEach((existingParticipant) => {
    participantsById.set(existingParticipant.id, existingParticipant);
  });
  participantsById.set(participant.id, participant);
  return sortParticipants(Array.from(participantsById.values()));
}

interface LocatedCard {
  card: Card;
  column: Column;
  cardIndex: number;
}

interface CardVoteState {
  id?: string;
  cardId?: string;
  votes?: number;
  viewerVoteCount?: number;
  votesHidden?: boolean;
  updatedAt?: string;
}

function findCardInColumns(columns: Column[], cardId: string): LocatedCard | null {
  for (const column of columns) {
    const cardIndex = column.cards.findIndex((card) => card.id === cardId);
    if (cardIndex !== -1) {
      return {
        card: column.cards[cardIndex],
        column,
        cardIndex,
      };
    }
  }
  return null;
}

function canUserMoveCard(card: Card, isAdmin: boolean, userId: string): boolean {
  return isAdmin || card.isAuthor === true || card.createdBy === userId;
}

function normalizeCardTargetIndex(targetIndex: number | undefined, maxIndex: number): number {
  if (targetIndex === undefined || !Number.isFinite(targetIndex)) {
    return maxIndex;
  }
  return Math.max(0, Math.min(Math.trunc(targetIndex), maxIndex));
}

function insertCardAtIndex(cards: Card[], card: Card, targetIndex: number): Card[] {
  const orderedCards = [...cards];
  orderedCards.splice(targetIndex, 0, card);
  return orderedCards;
}

function moveCardInColumns(
  columns: Column[],
  cardId: string,
  targetColumnId: string,
  targetIndex?: number
): Column[] {
  const locatedCard = findCardInColumns(columns, cardId);
  const targetColumn = columns.find((column) => column.id === targetColumnId);
  if (!locatedCard || !targetColumn) return columns;

  const sourceColumnId = locatedCard.column.id;
  const movedCard = { ...locatedCard.card, columnId: targetColumnId };

  if (sourceColumnId === targetColumnId) {
    const remainingCards = locatedCard.column.cards.filter((card) => card.id !== cardId);
    const normalizedIndex = normalizeCardTargetIndex(targetIndex, remainingCards.length);
    if (normalizedIndex === locatedCard.cardIndex) return columns;

    return columns.map((column) => (
      column.id === sourceColumnId
        ? { ...column, cards: insertCardAtIndex(remainingCards, movedCard, normalizedIndex) }
        : column
    ));
  }

  const normalizedIndex = normalizeCardTargetIndex(targetIndex, targetColumn.cards.length);
  return columns.map((column) => {
    if (column.id === sourceColumnId) {
      return { ...column, cards: column.cards.filter((card) => card.id !== cardId) };
    }
    if (column.id === targetColumnId) {
      return { ...column, cards: insertCardAtIndex(column.cards, movedCard, normalizedIndex) };
    }
    return column;
  });
}

function replaceCardInColumns(columns: Column[], replacementCard: Card): Column[] {
  return columns.map((column) => ({
    ...column,
    cards: column.cards.map((card) => (
      card.id === replacementCard.id ? replacementCard : card
    )),
  }));
}

function applyCardVoteStateToColumns(columns: Column[], voteState: CardVoteState): Column[] {
  const cardId = voteState.cardId ?? voteState.id;
  if (!cardId) return columns;

  return columns.map((column) => ({
    ...column,
    cards: column.cards.map((card) => {
      if (card.id !== cardId) return card;
      return {
        ...card,
        votes: voteState.votes !== undefined ? voteState.votes : card.votes,
        viewerVoteCount: voteState.viewerVoteCount !== undefined
          ? voteState.viewerVoteCount
          : card.viewerVoteCount,
        votesHidden: voteState.votesHidden !== undefined ? voteState.votesHidden : card.votesHidden,
        updatedAt: voteState.updatedAt ?? card.updatedAt,
      };
    }),
  }));
}

function readCardVoteState(payload: unknown): CardVoteState | null {
  if (!isRecord(payload)) return null;
  const source = isRecord(payload.card) ? payload.card : payload;

  return {
    id: typeof source.id === 'string' ? source.id : undefined,
    cardId: typeof source.cardId === 'string' ? source.cardId : undefined,
    votes: typeof source.votes === 'number' ? source.votes : undefined,
    viewerVoteCount: typeof source.viewerVoteCount === 'number' ? source.viewerVoteCount : undefined,
    votesHidden: typeof source.votesHidden === 'boolean' ? source.votesHidden : undefined,
    updatedAt: typeof source.updatedAt === 'string' ? source.updatedAt : undefined,
  };
}

function maskHiddenCardsForViewer(board: Board, userId: string): Board {
  if (board.settings.cardVisibility !== 'BLURRED') {
    return board;
  }

  return {
    ...board,
    columns: board.columns.map((column) => ({
      ...column,
      cards: column.cards.map((card) => {
        const isAuthor = card.isAuthor === true || card.createdBy === userId;
        return isAuthor ? card : {
          ...card,
          content: '[hidden]',
        };
      }),
    })),
  };
}

export function useBoard({ boardId, userId, userName }: UseBoardOptions): UseBoardReturn {
  const [board, setBoard] = useState<Board | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [participants, setParticipants] = useState<BoardParticipant[]>([]);
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [requiresUserName, setRequiresUserName] = useState(false);
  const [hasJoined, setHasJoined] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  const { isDuplicate, trackEvent } = useEventDeduplication({ timeWindowMs: 30000, maxTrackedEvents: 1000 });
  const optimisticCardsRef = useRef<Map<string, string>>(new Map());
  const boardRef = useRef<Board | null>(null);
  const userNameRef = useRef<string | undefined>(userName);

  useEffect(() => {
    boardRef.current = board;
  }, [board]);

  useEffect(() => {
    userNameRef.current = userName;
  }, [userName]);

  const refreshBoard = useCallback(async (revealCards = false): Promise<Board | null> => {
    if (!boardId) return null;
    const response = await boardApi.send<{ board: Board }>('getBoard', {
      boardId,
      revealCards,
    });
    setBoard(response.board);
    setParticipants(sortParticipants(response.board.participants ?? []));
    setIsAdmin(response.board.isAdmin === true || response.board.adminId === userId);
    return response.board;
  }, [boardId, userId]);

  const revealHiddenCards = useCallback(async (): Promise<void> => {
    await refreshBoard(true);
  }, [refreshBoard]);

  const hideHiddenCards = useCallback(async (): Promise<void> => {
    setBoard((prev) => prev ? maskHiddenCardsForViewer(prev, userId) : prev);
    await refreshBoard(false);
  }, [refreshBoard, userId]);

  const addCard = useCallback(async (columnId: string, content: string, author: string) => {
    if (!board) return;
    const optimisticId = 'optimistic-' + Date.now();
    const optimisticCard: Card = { id: optimisticId, content, author, createdBy: userId, columnId, votes: 0, createdAt: new Date().toISOString() };
    const dedupKey = `${columnId}:${content}:${author}`;
    trackEvent('cardCreated', { key: dedupKey });
    optimisticCardsRef.current.set(dedupKey, optimisticId);
    setBoard((prev) => prev ? { ...prev, columns: prev.columns.map((col) => col.id === columnId ? { ...col, cards: [...col.cards, optimisticCard] } : col) } : prev);
    try {
      const response = await boardApi.send<{ card: Card }>('addCard', { boardId, columnId, content, author });
      setBoard((prev) => prev ? { ...prev, columns: prev.columns.map((col) => col.id === columnId ? { ...col, cards: col.cards.map((card) => card.id === optimisticId ? response.card : card) } : col) } : prev);
      optimisticCardsRef.current.delete(dedupKey);
    } catch (err) {
      if (isUnjoinedConnectionError(err)) {
        try {
          const joinUserName = author !== 'Anonymous' ? author : undefined;
          const joinResponse = await boardApi.send<{ board?: Board }>('joinBoard', { boardId, userName: joinUserName });
          if (joinResponse.board) {
            setParticipants(sortParticipants(joinResponse.board.participants ?? []));
            setIsAdmin(joinResponse.board.isAdmin === true || joinResponse.board.adminId === userId);
          }
          setHasJoined(true);
          setRequiresPassword(false);
          setRequiresUserName(false);
          if (joinUserName) localStorage.setItem('retrojet-user-name', joinUserName);

          const retryResponse = await boardApi.send<{ card: Card }>('addCard', { boardId, columnId, content, author });
          setBoard((prev) => prev ? { ...prev, columns: prev.columns.map((col) => col.id === columnId ? { ...col, cards: col.cards.map((card) => card.id === optimisticId ? retryResponse.card : card) } : col) } : prev);
          optimisticCardsRef.current.delete(dedupKey);
          return;
        } catch (retryError) {
          setBoard((prev) => prev ? { ...prev, columns: prev.columns.map((col) => col.id === columnId ? { ...col, cards: col.cards.filter((card) => card.id !== optimisticId) } : col) } : prev);
          console.error('Failed to add card after joining board:', retryError);
          throw retryError;
        }
      }
      setBoard((prev) => prev ? { ...prev, columns: prev.columns.map((col) => col.id === columnId ? { ...col, cards: col.cards.filter((card) => card.id !== optimisticId) } : col) } : prev);
      console.error('Failed to add card:', err);
      throw err;
    }
  }, [board, boardId, trackEvent, userId]);

  const moveCard = useCallback(async (
    cardId: string,
    targetColumnId: string,
    targetIndex?: number
  ): Promise<void> => {
    if (!board) return;
    const locatedCard = findCardInColumns(board.columns, cardId);
    if (!locatedCard || !canUserMoveCard(locatedCard.card, isAdmin, userId)) return;

    const previousColumns = board.columns;
    const nextColumns = moveCardInColumns(previousColumns, cardId, targetColumnId, targetIndex);
    if (nextColumns === previousColumns) return;

    setBoard((prev) => prev ? {
      ...prev,
      columns: moveCardInColumns(prev.columns, cardId, targetColumnId, targetIndex),
    } : prev);

    try {
      const response = await boardApi.send<{ card?: Card }>('moveCard', {
        boardId,
        cardId,
        targetColumnId,
        targetIndex,
      });
      if (response.card) {
        const movedCard = response.card;
        setBoard((prev) => prev ? {
          ...prev,
          columns: replaceCardInColumns(prev.columns, movedCard),
        } : prev);
      }
    }
    catch (err) {
      setBoard((prev) => prev ? { ...prev, columns: previousColumns } : prev);
      console.error('Failed to move card:', err);
    }
  }, [board, boardId, isAdmin, userId]);

  const applyCardUpdate = useCallback((cardId: string, updates: Partial<Card>): void => {
    setBoard((prev) => prev ? { ...prev, columns: prev.columns.map((col) => ({ ...col, cards: col.cards.map((card) => card.id === cardId ? { ...card, ...updates } : card) })) } : prev);
  }, []);

  const updateCard = useCallback(async (cardId: string, content: string): Promise<void> => {
    if (!board) return;
    const trimmed = content.trim();
    if (!trimmed) return;

    const currentCard = board.columns.flatMap((column) => column.cards).find((card) => card.id === cardId);
    if (!currentCard) return;

    applyCardUpdate(cardId, { content: trimmed });
    try {
      const response = await boardApi.send<{ card: Card }>('updateCard', { boardId, cardId, content: trimmed });
      applyCardUpdate(response.card.id, response.card);
    } catch (err) {
      applyCardUpdate(cardId, { content: currentCard.content });
      console.error('Failed to update card:', err);
      throw err;
    }
  }, [applyCardUpdate, board, boardId]);

  const removeCard = useCallback((cardId: string) => {
    setBoard((prev) => prev ? { ...prev, columns: prev.columns.map((col) => ({ ...col, cards: col.cards.filter((card) => card.id !== cardId) })) } : prev);
  }, []);

  const deleteCard = useCallback(async (cardId: string): Promise<void> => {
    if (!board) return;
    const locatedCard = findCardInColumns(board.columns, cardId);
    if (!locatedCard || !canUserMoveCard(locatedCard.card, isAdmin, userId)) return;

    const previousColumns = board.columns;
    removeCard(cardId);

    try {
      await boardApi.send('deleteCard', { boardId, cardId });
    } catch (err) {
      setBoard((prev) => prev ? { ...prev, columns: previousColumns } : prev);
      console.error('Failed to delete card:', err);
      throw err;
    }
  }, [board, boardId, isAdmin, removeCard, userId]);

  const applyCardVoteState = useCallback((voteState: CardVoteState): void => {
    setBoard((prev) => prev ? {
      ...prev,
      columns: applyCardVoteStateToColumns(prev.columns, voteState),
    } : prev);
  }, []);

  const voteCard = useCallback(async (cardId: string): Promise<void> => {
    if (!board?.settings.votingEnabled) return;
    try {
      const response = await boardApi.send<{ card?: CardVoteState }>('voteCard', { boardId, cardId });
      if (response.card) {
        applyCardVoteState(response.card);
      }
    } catch (err) {
      console.error('Failed to vote for card:', err);
      throw err;
    }
  }, [applyCardVoteState, board?.settings.votingEnabled, boardId]);

  const removeCardVote = useCallback(async (cardId: string): Promise<void> => {
    if (!board?.settings.votingEnabled) return;
    try {
      const response = await boardApi.send<{ card?: CardVoteState }>('removeCardVote', { boardId, cardId });
      if (response.card) {
        applyCardVoteState(response.card);
      }
    } catch (err) {
      console.error('Failed to remove card vote:', err);
      throw err;
    }
  }, [applyCardVoteState, board?.settings.votingEnabled, boardId]);

  const addColumn = useCallback(async (name: string, color: string) => {
    if (!board || !isAdmin) return;
    try {
      const response = await boardApi.send<{ column: Column }>('addColumn', { boardId, name, color });
      setBoard((prev) => {
        if (!prev || prev.columns.some((col) => col.id === response.column.id)) return prev;
        return { ...prev, columns: [...prev.columns, { ...response.column, cards: [], groups: [] }] };
      });
    } catch (err) { console.error('Failed to add column:', err); throw err; }
  }, [board, boardId, isAdmin]);

  const updateColumn = useCallback(async (columnId: string, name: string, color: string) => {
    if (!board || !isAdmin) return;
    try {
      await boardApi.send('updateColumn', { boardId, columnId, name, color });
      setBoard((prev) => prev ? { ...prev, columns: prev.columns.map((col) => col.id === columnId ? { ...col, name, color } : col) } : prev);
    } catch (err) { console.error('Failed to update column:', err); throw err; }
  }, [board, boardId, isAdmin]);

  const deleteColumn = useCallback(async (columnId: string, migrateToColumnId?: string) => {
    if (!board || !isAdmin) return;
    try {
      await boardApi.send('deleteColumn', { boardId, columnId, migrateToColumnId });
      setBoard((prev) => {
        if (!prev) return prev;
        const columnToDelete = prev.columns.find((c) => c.id === columnId);
        let updatedColumns = prev.columns.filter((c) => c.id !== columnId);
        if (migrateToColumnId && columnToDelete) {
          updatedColumns = updatedColumns.map((col) => col.id === migrateToColumnId ? { ...col, cards: [...col.cards, ...columnToDelete.cards] } : col);
        }
        return { ...prev, columns: updatedColumns };
      });
      if (migrateToColumnId) {
        await refreshBoard();
      }
    } catch (err) { console.error('Failed to delete column:', err); throw err; }
  }, [board, boardId, isAdmin, refreshBoard]);

  const reorderColumns = useCallback(async (columnIds: string[]) => {
    if (!board || !isAdmin) return;
    setBoard((prev) => {
      if (!prev) return prev;
      const columnMap = new Map(prev.columns.map((c) => [c.id, c]));
      const reorderedColumns = columnIds.map((id, index) => { const col = columnMap.get(id); return col ? { ...col, order: index } : null; }).filter((c): c is Column => c !== null);
      return { ...prev, columns: reorderedColumns };
    });
    try { await boardApi.send('reorderColumns', { boardId, columnIds }); }
    catch (err) { await refreshBoard().catch(console.error); console.error('Failed to reorder columns:', err); throw err; }
  }, [board, boardId, isAdmin, refreshBoard]);

  const updateBoardSettings = useCallback(async (settings: BoardSettingsUpdate) => {
    if (!board || !isAdmin) return;
    try {
      const response = await boardApi.send<{ board?: Board }>('updateBoardSettings', { boardId, settings });
      if (response.board) {
        setBoard(response.board);
        setIsAdmin(response.board.isAdmin === true || response.board.adminId === userId);
      } else {
        setBoard((prev) => prev ? { ...prev, settings: { ...prev.settings, ...toBoardSettingsPatch(settings) } } : prev);
      }
    } catch (err) { console.error('Failed to update board settings:', err); throw err; }
  }, [board, boardId, isAdmin, userId]);

  const promoteToAdmin = useCallback(async (adminPassword: string): Promise<void> => {
    if (!board) return;
    try {
      const response = await boardApi.send<{ adminId?: string }>('promoteToAdmin', { boardId, adminPassword });
      const promotedAdminId = response.adminId ?? userId;
      setBoard((prev) => prev ? { ...prev, adminId: promotedAdminId, isAdmin: promotedAdminId === userId } : prev);
      setIsAdmin(promotedAdminId === userId);
      void refreshBoard().catch((err) => console.error('Failed to refresh board after admin promotion:', err));
    } catch (err) {
      console.error('Failed to become board admin:', err);
      throw err;
    }
  }, [board, boardId, refreshBoard, userId]);

  const mergeCards = useCallback(async (sourceCardId: string, targetCardId: string) => {
    if (!board || !isAdmin || !board.settings.enableMergeCards) return;
    try {
      const response = await boardApi.send<{ board?: Board }>('mergeCards', { boardId, sourceCardId, targetCardId });
      if (response.board) {
        setBoard(response.board);
        setIsAdmin(response.board.isAdmin === true || response.board.adminId === userId);
        return;
      }
      setBoard((prev) => prev ? { ...prev, columns: prev.columns.map((col) => ({ ...col, cards: col.cards.filter((card) => card.id !== sourceCardId) })) } : prev);
      await refreshBoard();
    } catch (err) { console.error('Failed to merge cards:', err); throw err; }
  }, [board, boardId, isAdmin, refreshBoard, userId]);

  const joinBoard = useCallback(async (password?: string, userName?: string) => {
    setJoinError(null);
    try {
      const response = await boardApi.send<{ board?: Board }>('joinBoard', { boardId, password, userName });
      if (response.board) {
        setBoard(response.board);
        setParticipants(sortParticipants(response.board.participants ?? []));
        setIsAdmin(response.board.isAdmin === true || response.board.adminId === userId);
      }
      setHasJoined(true); setRequiresPassword(false); setRequiresUserName(false);
      if (userName) localStorage.setItem('retrojet-user-name', userName);
    } catch (err) { const message = err instanceof Error ? err.message : 'Failed to join board'; setJoinError(message); throw err; }
  }, [boardId, userId]);

  const updateUserBoardName = useCallback(async (nextUserName: string): Promise<string> => {
    const trimmedUserName = nextUserName.trim();
    if (!trimmedUserName) {
      throw new Error('User name is required');
    }

    const response = await boardApi.send<{ userName?: string }>('updateUserBoardName', {
      boardId,
      userName: trimmedUserName,
    });
    const savedUserName = response.userName ?? trimmedUserName;
    localStorage.setItem('retrojet-user-name', savedUserName);
    setParticipants((prev) => upsertParticipant(prev, {
      id: userId,
      name: savedUserName,
      isAdmin: isAdmin || boardRef.current?.adminId === userId,
    }));
    return savedUserName;
  }, [boardId, isAdmin, userId]);

  useEffect(() => {
    if (!boardId || !userId) return;
    let isActive = true;

    const fetchBoard = async () => {
      setIsLoading(true); setError(null);
      try {
        await boardApi.connect(boardId, userId);
        if (!isActive) return;
        const loadedBoard = await refreshBoard();
        if (!isActive || !loadedBoard) return;
        const currentUserName = userNameRef.current;
        const storedUserName = currentUserName && currentUserName !== 'Anonymous'
          ? currentUserName
          : localStorage.getItem('retrojet-user-name') ?? undefined;
        if (loadedBoard.hasPassword) setRequiresPassword(true);
        if (loadedBoard.settings?.requireNameOnJoin && !storedUserName) setRequiresUserName(true);
        if (!loadedBoard.hasPassword && (!loadedBoard.settings?.requireNameOnJoin || storedUserName)) {
          if (!isActive) return;
          try { await joinBoard(undefined, storedUserName); } catch { /* silent */ }
        }
      } catch (err) {
        if (isActive) {
          setError(err instanceof Error ? err.message : 'Failed to load board');
        }
      }
      finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };
    fetchBoard();
    const unsubConnectionChange = boardApi.onConnectionChange((connected: boolean) => {
      setIsConnected(connected);
      setReconnectAttempts(boardApi.currentReconnectAttempts);
      if (connected && boardRef.current) {
        void refreshBoard().catch((err) => console.error('Failed to refresh board after reconnect:', err));
      }
    });
    const unsubUserJoined = boardApi.onEvent('userJoined', (eventPayload: unknown) => {
      const payload = readParticipantEventPayload(eventPayload);
      if (!payload.userId) {
        void refreshBoard().catch((err) => console.error('Failed to refresh participants after user joined:', err));
        return;
      }
      const joinedUserId = payload.userId;
      setParticipants((prev) => upsertParticipant(prev, {
        id: joinedUserId,
        name: normalizeParticipantName(payload.userName),
        isAdmin: payload.role === 'admin' || joinedUserId === boardRef.current?.adminId,
      }));
    });
    const unsubUserLeft = boardApi.onEvent('userLeft', (eventPayload: unknown) => {
      const payload = readParticipantEventPayload(eventPayload);
      if (!payload.userId) {
        void refreshBoard().catch((err) => console.error('Failed to refresh participants after user left:', err));
        return;
      }
      const leftUserId = payload.userId;
      setParticipants((prev) => prev.filter((participant) => participant.id !== leftUserId));
    });
    const unsubUserBoardNameUpdated = boardApi.onEvent('userBoardNameUpdated', (eventPayload: unknown) => {
      const payload = readParticipantEventPayload(eventPayload);
      const participantUserId = payload.userId ?? (payload.isSelf ? userId : undefined);
      if (!participantUserId) {
        void refreshBoard().catch((err) => console.error('Failed to refresh participants after name update:', err));
        return;
      }
      setParticipants((prev) => upsertParticipant(prev, {
        id: participantUserId,
        name: normalizeParticipantName(payload.userName),
        isAdmin: prev.find((participant) => participant.id === participantUserId)?.isAdmin
          ?? participantUserId === boardRef.current?.adminId,
      }));
    });
    const unsubCardCreated = boardApi.onEvent('cardCreated', (eventPayload: unknown) => {
      const payload = eventPayload as { card: Card }; if (!payload.card) return;
      const dedupKey = `${payload.card.columnId}:${payload.card.content}:${payload.card.author}`;
      if (isDuplicate('cardCreated', { key: dedupKey })) {
        const optimisticId = optimisticCardsRef.current.get(dedupKey);
        if (optimisticId) { setBoard((prev) => prev ? { ...prev, columns: prev.columns.map((col) => ({ ...col, cards: col.cards.map((card) => card.id === optimisticId ? payload.card : card) })) } : prev); optimisticCardsRef.current.delete(dedupKey); return; }
      }
      setBoard((prev) => { if (!prev) return prev; const column = prev.columns.find((c) => c.id === payload.card.columnId); if (column && !column.cards.some((card) => card.id === payload.card.id)) { return { ...prev, columns: prev.columns.map((col) => col.id === payload.card.columnId ? { ...col, cards: [...col.cards, payload.card] } : col) }; } return prev; });
      trackEvent('cardCreated', { key: dedupKey });
    });
    const unsubCardUpdated = boardApi.onEvent('cardUpdated', (eventPayload: unknown) => { const payload = eventPayload as { card: Card }; if (payload.card) applyCardUpdate(payload.card.id, payload.card); });
    const unsubCardDeleted = boardApi.onEvent('cardDeleted', (eventPayload: unknown) => { const payload = eventPayload as { cardId: string }; if (payload.cardId) removeCard(payload.cardId); });
    const unsubCardMoved = boardApi.onEvent('cardMoved', (eventPayload: unknown) => {
      const payload = eventPayload as { cardId?: string; targetColumnId?: string; targetIndex?: number };
      const movedCardId = payload.cardId;
      const targetColumnId = payload.targetColumnId;
      if (!movedCardId || !targetColumnId) return;
      setBoard((prev) => prev ? {
        ...prev,
        columns: moveCardInColumns(prev.columns, movedCardId, targetColumnId, payload.targetIndex),
      } : prev);
    });
    const unsubCardVoted = boardApi.onEvent('cardVoted', (eventPayload: unknown) => {
      const voteState = readCardVoteState(eventPayload);
      if (voteState) {
        applyCardVoteState(voteState);
      }
    });
    const unsubColumnCreated = boardApi.onEvent('columnCreated', (eventPayload: unknown) => { const payload = eventPayload as { column: Column }; if (!payload.column) return; setBoard((prev) => { if (!prev || prev.columns.some((c) => c.id === payload.column.id)) return prev; return { ...prev, columns: [...prev.columns, { ...payload.column, cards: [], groups: [] }] }; }); });
    const unsubColumnUpdated = boardApi.onEvent('columnUpdated', (eventPayload: unknown) => { const payload = eventPayload as { column: Column }; if (!payload.column) return; setBoard((prev) => prev ? { ...prev, columns: prev.columns.map((col) => col.id === payload.column.id ? { ...col, ...payload.column } : col) } : prev); });
    const unsubColumnDeleted = boardApi.onEvent('columnDeleted', (eventPayload: unknown) => {
      const payload = eventPayload as { columnId: string; migrateToColumnId?: string };
      if (!payload.columnId) return;
      setBoard((prev) => prev ? { ...prev, columns: prev.columns.filter((c) => c.id !== payload.columnId) } : prev);
      if (payload.migrateToColumnId) {
        void refreshBoard().catch((err) => console.error('Failed to refresh board after column deletion:', err));
      }
    });
    const handleColumnsReordered = (eventPayload: unknown) => { const payload = eventPayload as { columnIds: string[] }; if (!payload.columnIds) return; setBoard((prev) => { if (!prev) return prev; const columnMap = new Map(prev.columns.map((c) => [c.id, c])); const reorderedColumns = payload.columnIds.map((id, index) => { const col = columnMap.get(id); return col ? { ...col, order: index } : null; }).filter((c): c is Column => c !== null); return { ...prev, columns: reorderedColumns }; }); };
    const handleBoardSettingsUpdated = (eventPayload: unknown) => {
      const payload = eventPayload as { settings: BoardSettings };
      if (!payload.settings) return;
      setBoard((prev) => prev ? { ...prev, settings: { ...prev.settings, ...payload.settings } } : prev);
      void refreshBoard().catch((err) => console.error('Failed to refresh board after settings update:', err));
    };
    const unsubColumnsReordered = boardApi.onEvent('columnsReordered', handleColumnsReordered);
    const unsubLegacyColumnMoved = boardApi.onEvent('columnMoved', handleColumnsReordered);
    const unsubBoardSettingsUpdated = boardApi.onEvent('boardSettingsUpdated', handleBoardSettingsUpdated);
    const unsubLegacyBoardSettingsChanged = boardApi.onEvent('boardSettingsChanged', handleBoardSettingsUpdated);
    return () => { isActive = false; boardApi.disconnect(); unsubConnectionChange(); unsubUserJoined(); unsubUserLeft(); unsubUserBoardNameUpdated(); unsubCardCreated(); unsubCardUpdated(); unsubCardDeleted(); unsubCardMoved(); unsubCardVoted(); unsubColumnCreated(); unsubColumnUpdated(); unsubColumnDeleted(); unsubColumnsReordered(); unsubLegacyColumnMoved(); unsubBoardSettingsUpdated(); unsubLegacyBoardSettingsChanged(); };
  }, [boardId, userId, applyCardUpdate, applyCardVoteState, removeCard, refreshBoard, isDuplicate, trackEvent, joinBoard]);

  return { board, isLoading, error, isConnected, reconnectAttempts, isAdmin, participants, requiresPassword, requiresUserName, hasJoined, joinError, revealHiddenCards, hideHiddenCards, addCard, moveCard, updateCard, deleteCard, removeCard, voteCard, removeCardVote, addColumn, updateColumn, deleteColumn, reorderColumns, updateBoardSettings, promoteToAdmin, mergeCards, joinBoard, updateUserBoardName };
}
